import { useState } from "react";
import { ContactRow } from "../../api/relationship.type";
import { SubmitButton } from "../../components/shared/buttons/submit-button";
import Card from "../../components/shared/card";
import Typography from "../../components/shared/typography";
import { SignupAuthProvider } from "../../hooks/SignupAuthContext";
import { useLoadInviteDetails } from "../../hooks/useLoadInviteDetails";
import AppDownloadButtons from "../shared/app-download-buttons";
import { ThemedSpinner } from "../shared/spinner";
import MutualContactItem from "./mutual-contact-item";
import SignupForm from "./signup-form";

function InviteAcceptLayout({ slug }: { slug?: string }) {
  const headerHeight = "4rem";

  const { inviteDetails, isLoading } = useLoadInviteDetails(slug || "");

  const [isAccepting, setIsAccepting] = useState(false);

  const isRegistered = Boolean(
    (inviteDetails?.contact?.related_user as any)?.is_registered
  );

  // inviteDetails?.contact.related_user is your account info
  // inviteDetails?.reciprocal.related_user is the user who invited you
  // inviteDetails?.contact.related_user.is_registered is whether the user is registered

  return (
    <div
      className="flex flex-col items-center justify-start min-h-screen w-full p-4 pt-0 bg-gradient-to-b from-cream to-terracotta"
      style={{ paddingTop: headerHeight }}
    >
      <div className="w-full max-w-md mb-8 text-center">
        <div className="flex justify-center mb-4"></div>

        {isLoading && <ThemedSpinner size="lg" />}
        {!isLoading && !inviteDetails && (
          <Card
            className="w-full max-w-md mx-auto mb-6"
            variant="terracotta"
            elevation={2}
          >
            <Card.Body>
              <Card.Header>No invite details found</Card.Header>
              <Typography variant="body1" className="text-cream">
                Please reach out to the user who invited you to resend the
                invite.
              </Typography>
            </Card.Body>
          </Card>
        )}

        {inviteDetails && !isRegistered && (
          <Typography variant="h2" className="mb-2 text-primary">
            {inviteDetails?.reciprocal?.related_user?.first_name} has invited
            you to join Harper.
          </Typography>
        )}
        {inviteDetails && isRegistered && (
          <Typography variant="h2" className="mb-2 text-primary">
            You're already registered with Harper.
          </Typography>
        )}
        {!isAccepting &&
          inviteDetails?.mutuals?.length &&
          inviteDetails.mutuals.length > 0 && (
            <Typography variant="body1" className="text-terracotta-500 mb-2">
              You will be able to view the following accounts:
            </Typography>
          )}
      </div>

      {inviteDetails?.mutuals?.length && inviteDetails.mutuals.length > 0 && (
        <Card
          className="w-full max-w-md mx-auto mb-6"
          variant="cream"
          elevation={2}
        >
          <Card.Body>
            <ul className="list-disc pl-5 space-y-2">
              {inviteDetails?.mutuals
                ?.filter(
                  // Remove the user who invited you. Also all the contacts are returned (including the contact with the user who invited you and the mutual contact and we only want the mutual contact)
                  (acc: ContactRow) =>
                    acc.user_id !== inviteDetails.reciprocal.related_user.id
                )
                .filter(
                  (acc: ContactRow, index: number, array: ContactRow[]) =>
                    // Remove duplicates by keeping only the first occurrence of each related_user.id
                    array.findIndex(
                      (item) => item.related_user?.id === acc.related_user?.id
                    ) === index
                )
                .map((acc: ContactRow) => (
                  <MutualContactItem key={acc?.id} contact={acc} />
                ))}
            </ul>
          </Card.Body>
        </Card>
      )}

      {inviteDetails && !isAccepting && !isRegistered && (
        <div className="w-full max-w-md">
          <SubmitButton
            onClick={() => setIsAccepting(true)}
            className="w-full"
            isDisabled={!slug}
          >
            Accept Invite
          </SubmitButton>
        </div>
      )}
      {inviteDetails && isRegistered && (
        <div className="w-full max-w-md">
          <Card className="w-full" variant="cream" elevation={2}>
            <Card.Body>
              <Typography variant="body1" className="text-terracotta-500 mb-4">
                Your Harper account is already set up. Please download the app
                and log in to continue. If you signed up with your phone number,
                use your phone number to log in.
              </Typography>
              <div className="space-y-4">
                <AppDownloadButtons size="small" layout="horizontal" />
                <a
                  href={`/dl/account${slug ? `?invite-slug=${slug}` : ""}`}
                  className="flex justify-center"
                >
                  <div
                    className="bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium py-3 px-6 rounded-lg transition-colors cursor-pointer text-center"
                    style={{
                      width: 164,
                      height: 55,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    Open App Login
                  </div>
                </a>
              </div>
            </Card.Body>
          </Card>
        </div>
      )}
      {isAccepting && (
        <div className="w-full max-w-md">
          <SignupAuthProvider>
            <SignupForm
              onToggleView={() => setIsAccepting(false)}
              presetValues={{
                firstName: inviteDetails?.contact?.related_user
                  ?.first_name as string,
                lastName: inviteDetails?.contact?.related_user
                  ?.last_name as string,
                email: inviteDetails?.contact?.related_user?.email ?? undefined,
                phone: inviteDetails?.contact?.related_user?.phone ?? undefined,
                existingUserId: inviteDetails?.contact?.related_user?.id,
                slug,
              }}
            />
          </SignupAuthProvider>
        </div>
      )}

      {!isAccepting && !isRegistered && (
        <div className="w-full max-w-md mt-20 text-center">
          <Typography variant="h5" className="text-primary mb-4">
            What is Harper?
          </Typography>
          <Typography variant="body2" className="text-cream-200 mb-4">
            Harper is your personal AI medical assistant that helps you manage
            your health information, communicate with healthcare providers, and
            get answers to your medical questions.
          </Typography>
          <Typography variant="body2" className="text-cream-200 mb-4">
            By accepting this invite, you'll be able to securely share and
            access medical records with trusted contacts while maintaining full
            control over your privacy and data.
          </Typography>
        </div>
      )}

      {!isAccepting && !isRegistered && (
        <div className="w-full max-w-md mt-8 text-center">
          <Typography variant="caption" className="text-cream">
            © {new Date().getFullYear()} Harper. All rights reserved.
          </Typography>
        </div>
      )}
    </div>
  );
}

export default InviteAcceptLayout;
